import { emailSchema } from '@common/neo.tax';
import { z } from 'zod';

import { CompanyScope, AddOrganizationInvitationInput, OrganizationScope } from '@neo.tax/shared/graphql';
import { OrganizationMemberItemScope, OrganizationMemberTableItemMember } from '@neo.tax/shared/types';

// Invitations now require an expiration window. Admins pick between the two supported values
// at invite time; the backend validates these too (see AddOrganizationInvitationInput).
// Values are strings because react-hook-form's ControlledRadioGroup wires raw strings through;
// we coerce to `7 | 30` before handing the input to the GraphQL mutation.
const DEFAULT_EXPIRATION_DAYS = '7' as const;

export const organizationMemberFormSchema = z
  .object({
    id: z.string().optional(),
    email: emailSchema,
    scope: z.enum(OrganizationMemberItemScope),
    companyIds: z.array(z.string()),
    expirationDays: z.union([z.literal('7'), z.literal('30')]),
  })
  .refine(
    ({ companyIds, scope }) => {
      // if non admin, companyIds must not be empty
      if (scope !== OrganizationMemberItemScope.ADMIN && companyIds.length > 0) {
        return true;
      }
      // if admin, companyIds must be empty
      if (scope === OrganizationMemberItemScope.ADMIN && companyIds.length === 0) {
        return true;
      }

      // companyIds should contain one entry
      if (companyIds.length === 1) {
        return true;
      }
      return false;
    },
    {
      path: ['companyIds'],
      error: 'Company selection is only allowed for Company Admins',
    },
  );

export type OrganizationMemberFormType = z.infer<typeof organizationMemberFormSchema>;

export const makeDefaultFormValues = (member?: OrganizationMemberTableItemMember): OrganizationMemberFormType => {
  if (!member) {
    return {
      email: '',
      scope: OrganizationMemberItemScope.ADMIN,
      companyIds: [],
      expirationDays: DEFAULT_EXPIRATION_DAYS,
    };
  }
  return {
    id: member.id,
    email: member.email,
    scope: member.scope,
    companyIds: member.companies.map((company) => company.id),
    expirationDays: DEFAULT_EXPIRATION_DAYS,
  };
};

export const convertFormStateToOrganizationInvitationInput = (
  formState: OrganizationMemberFormType,
): AddOrganizationInvitationInput => {
  const expirationDays = Number.parseInt(formState.expirationDays, 10);
  if (formState.scope === OrganizationMemberItemScope.ADMIN) {
    return {
      email: formState.email,
      organizationScope: OrganizationScope.OrganizationAdmin,
      expirationDays,
    };
  }
  return {
    email: formState.email,
    organizationScope: OrganizationScope.OrganizationMember,
    companyIds: formState.companyIds,
    companyScope:
      formState.scope === OrganizationMemberItemScope.MEMBER ? CompanyScope.CompanyMember : CompanyScope.NonFinancial,
    expirationDays,
  };
};
